import {
  type OnChangeFn,
  type RowData,
  type Table,
  type TableFeature,
  type Updater,
  functionalUpdate,
  makeStateUpdater,
} from "@tanstack/react-table";

export interface RowLoadingTableState {
  loadingRows: Record<string, boolean>;
}

export interface RowLoadingOptions {
  enableRowLoading?: boolean;
  onRowLoadingChange?: OnChangeFn<Record<string, boolean>>;
}

export interface RowLoadingInstance {
  setLoadingRow: (updater: Updater<Record<string, boolean>>) => void;
  getLoadingRows: () => Record<string, boolean>;
  isRowLoading: (rowId: string) => boolean;
}

declare module "@tanstack/react-table" {
  interface TableState extends RowLoadingTableState {}
  interface TableOptionsResolved<TData extends RowData>
    extends RowLoadingOptions {}
  interface Table<TData extends RowData> extends RowLoadingInstance {}
}

export const RowLoadingFeature: TableFeature<any> = {
  getInitialState: (state): RowLoadingTableState => {
    return {
      loadingRows: {},
      ...state,
    };
  },
  getDefaultOptions: <TData extends RowData>(
    table: Table<TData>,
  ): RowLoadingOptions => {
    return {
      enableRowLoading: true,
      onRowLoadingChange: makeStateUpdater("loadingRows", table),
    } as RowLoadingOptions;
  },
  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.setLoadingRow = (updater) => {
      const safeUpdater: Updater<Record<string, boolean>> = (old) => {
        const newState = functionalUpdate(updater, old);
        return newState;
      };
      return table.options.onRowLoadingChange?.(safeUpdater);
    };
    table.getLoadingRows = () => {
      return table.getState().loadingRows;
    };
    table.isRowLoading = (rowId: string) => {
      return table.getState().loadingRows[rowId] ?? false;
    };
  },
};
